// src/app/core/services/navbar-behavior.service.ts

import {Injectable, Renderer2, Inject, OnDestroy} from '@angular/core';
import {DOCUMENT} from '@angular/common';
import {debounceTime, fromEvent, Subject} from 'rxjs';
import {BreakpointObserver, Breakpoints} from '@angular/cdk/layout';
import {takeUntil} from 'rxjs/operators';
import {Router} from "@angular/router";
import {environment} from "../../../environments/environment";

@Injectable({
  providedIn: 'root'
})
export class NavbarBehaviorService implements OnDestroy {
  private destroyed = new Subject<void>();
  private changeHeaderOn = 200;
  private isMobile = false;
  private originalPaddingTop = '';
  private scrollListener: Function | null = null;
  currentScreenSize: string | undefined;
  public isPeelHaltonSite = false;


  // Create a map to display breakpoint names
  private displayNameMap = new Map([
    [Breakpoints.XSmall, 'XSmall'],
    [Breakpoints.Small, 'Small'],
    [Breakpoints.Medium, 'Medium'],
    [Breakpoints.Large, 'Large'],
    [Breakpoints.XLarge, 'XLarge'],
  ]);


  constructor(
    private router: Router,
    private breakpointObserver: BreakpointObserver,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private document: Document
  ) {
    // Check if current route is part of PeelHalton
    this.isPeelHaltonSite = environment.hostname.includes('oakville');
    console.log('isPeelHaltonSite: ', this.isPeelHaltonSite);
  }

  public initialize() {

    // Set up breakpoint observer for mobile detection
    this.setupBreakpointObserver();

    // Store the original padding-top value
    this.originalPaddingTop = this.document.body.style.paddingTop;

    // Initial padding setup based on screen size
    this.updatePaddingForScreenSize();

    // Add class for other styling needs
    this.renderer.addClass(this.document.body, 'listing-details-page');

    // Initialize navbar state
    this.updateHeaderOnScroll();

    // Add listener for mobile menu toggle
    this.setupMobileMenuListener();

    // Add scroll listener to window
    this.addScrollListener();
  }

  // Add a proper scroll listener to the window
  private addScrollListener(): void {
    // Remove any existing listener first
    this.removeScrollListener();

    // Add the new listener using Renderer2
    /*this.scrollListener = this.renderer.listen('window', 'scroll', () => {
      this.updateHeaderOnScroll();
    });*/
    fromEvent(window, 'scroll')
      .pipe(
        debounceTime(10), // Small debounce time to prevent too many calculations
        takeUntil(this.destroyed)
      )
      .subscribe(() => {
        this.updateHeaderOnScroll();
      });

  }

  // Remove the scroll listener
  private removeScrollListener(): void {
    if (this.scrollListener) {
      this.scrollListener();
      this.scrollListener = null;
    }
  }


  /**
   * Set up the breakpoint observer for mobile detection
   */
  private setupBreakpointObserver(): void {
    this.breakpointObserver
      .observe([
        Breakpoints.XSmall,
        Breakpoints.Small,
        Breakpoints.Medium,
        Breakpoints.Large,
        Breakpoints.XLarge,
      ])
      .pipe(
        debounceTime(100),
        takeUntil(this.destroyed)
      )
      .subscribe((result) => {
        for (const query of Object.keys(result.breakpoints)) {
          if (result.breakpoints[query]) {
            this.currentScreenSize =
              this.displayNameMap.get(query) ?? 'Unknown';
            // console.log('screen size: ' + this.currentScreenSize);
            // Check if the current screen size is mobile (XSmall or Small)
            this.isMobile =
              this.currentScreenSize === 'XSmall' ||
              this.currentScreenSize === 'Small';

            // Update padding if needed after screen size change
            this.updatePaddingForScreenSize();

          }
        }
      });
  }

  // Method to update padding based on screen size
  private updatePaddingForScreenSize() {
    if (this.isMobile) {
      this.renderer.addClass(this.document.body, 'mobile-padding');
      this.renderer.removeClass(this.document.body, 'desktop-padding');
    } else {
      this.renderer.addClass(this.document.body, 'desktop-padding');
      this.renderer.removeClass(this.document.body, 'mobile-padding');
    }
  }

  // Add a listener for the mobile menu toggle to apply proper background
  private setupMobileMenuListener() {
    // Use a MutationObserver to watch for changes to the DOM
    const observer = new MutationObserver((mutations) => {
      // Check if our elements exist after DOM changes
      this.tryAttachMobileMenuListener();
    });

    // Start observing the document with the configured parameters
    observer.observe(this.document.body, {
      childList: true,
      subtree: true
    });

    // Also try at various intervals
    this.tryAttachMobileMenuListener();

    // Try again after the component has fully rendered
    setTimeout(() => {
      this.tryAttachMobileMenuListener();
    }, 500);

    setTimeout(() => {
      this.tryAttachMobileMenuListener();
    }, 2000);

    // Cleanup function to disconnect the observer when the component is destroyed
    this.destroyed.subscribe(() => {
      observer.disconnect();
    });
  }

  private tryAttachMobileMenuListener() {

    // Try different possible selectors for the toggle button
    const toggleSelectors = [
      '.navbar-toggle',
      'button.navbar-toggle',
      '[data-toggle="collapse"]',
      '.navbar-header button',
      'button[aria-controls="navbar"]',
      'button[aria-expanded]'
    ];

    // Explicitly type toggleButton as HTMLElement or null
    let toggleButton: HTMLElement | null = null;

    // Try each selector until we find a match
    for (const selector of toggleSelectors) {
      const elements = this.document.querySelectorAll(selector);

      if (elements.length > 0) {
        toggleButton = elements[0] as HTMLElement;
        break;
      }
    }

    // Try finding navbar elements
    const navbarSelectors = [
      '.navbar-default',
      '.navbar',
      'nav',
      'header nav',
      'header'
    ];

    let navbarElement: HTMLElement | null = null;

    for (const selector of navbarSelectors) {
      const elements = this.document.querySelectorAll(selector);

      if (elements.length > 0) {
        navbarElement = elements[0] as HTMLElement;
        break;
      }
    }
    const navCollapse = this.document.querySelector('.navbar-collapse, .collapse') as HTMLElement | null;

    if (this.isPeelHaltonSite && navCollapse) {
      // Initially hide the menu for PeelHalton sites
      navCollapse.style.display = 'none';
    }

    // If we found both elements and haven't already attached a listener
    if (toggleButton && navbarElement && !toggleButton.hasAttribute('data-menu-listener-attached')) {
      console.log('Attaching mobile menu listener to button:', toggleButton);

      // Mark this button as having a listener attached
      toggleButton.setAttribute('data-menu-listener-attached', 'true');

      // Initially set background based on mobile status
      this.updateNavbarBackground(navbarElement);

      // Add the click listener
      toggleButton.addEventListener('click', (event) => {
        if (this.isPeelHaltonSite) {
          console.log('PeelHalton site detected, not updating navbar background');
          event.preventDefault(); // Prevent default toggle behavior
          return; // Don't proceed with toggle for PeelHalton sites
        }

        // Add a slight delay to check the state after the Bootstrap toggle has run
        setTimeout(() => {
          // Try multiple ways to determine if the menu is expanded
          const isCollapsed = navCollapse?.classList.contains('collapse') && !navCollapse?.classList.contains('in');
          const isExpanded = !isCollapsed || toggleButton?.getAttribute('aria-expanded') === 'true';

          if (isExpanded) {
            // Menu is open - always add solid background
            navbarElement.classList.add('navbar-solid-background');
          } else {
            // Menu is closed - update background based on scroll position and mobile status
            this.updateNavbarBackground(navbarElement);
          }
        }, 50);
      });

      // Also listen for scroll events to update the navbar background
      window.addEventListener('scroll', () => {
        this.updateNavbarBackground(navbarElement);
      });

      // And listen for resize events to re-check mobile status
      window.addEventListener('resize', () => {
        this.updateNavbarBackground(navbarElement);
      });

      // Also add CSS for the navbar background if not already present
      this.ensureNavbarStylesExist();

      return true; // Successfully attached
    } else {
      return false; // Failed to attach
    }
  }

// Update navbar background based on scroll position and mobile status
  private updateNavbarBackground(navbarElement: HTMLElement) {
    // Check if we're on mobile
    const isMobile = this.isMobile;

    // Check scroll position
    const isScrolled = window.scrollY >= this.changeHeaderOn;

    // If either scrolled down OR on mobile, use solid background
    if (isScrolled || isMobile) {
      navbarElement.classList.add('navbar-solid-background');
    } else {
      // Only if we're both not scrolled AND not on mobile, use transparent background
      navbarElement.classList.remove('navbar-solid-background');
    }
  }

  private ensureNavbarStylesExist() {
    // Check if our style already exists
    if (!this.document.querySelector('#navbar-solid-background-style')) {
      const styleElement = this.document.createElement('style');
      styleElement.id = 'navbar-solid-background-style';
      styleElement.textContent = `
      .navbar-solid-background {
        background-color: #222 !important;
        transition: background-color 0.3s ease;
      }
    `;
      this.document.head.appendChild(styleElement);
    }
  }

  // Method to update header on scroll
  public updateHeaderOnScroll(): void {
    const header = this.document.querySelector('.navbar, .navbar-default, header') as HTMLElement;
    if (!header) return;

    const scrollY = this.scrollY();

    if (this.isMobile) return;

    if (scrollY >= this.changeHeaderOn) {
      header.classList.add('navbar-shrink');
      header.classList.add('bg-darkest-gray');
    } else {
      header.classList.remove('navbar-shrink');
      header.classList.remove('bg-darkest-gray');
    }
  }


  private scrollY() {
    return window.scrollY || window.pageYOffset || document.documentElement.scrollTop;
  }

  ngOnDestroy(): void {
    // Clean up the observer and listeners
    this.destroyed.next();
    this.destroyed.complete();

    // Remove scroll listener
    this.removeScrollListener();

    // remove the padding class
    this.renderer.removeClass(this.document.body, 'desktop-padding');
    this.renderer.removeClass(this.document.body, 'mobile-padding');

    // Remove body class
    this.renderer.removeClass(this.document.body, 'listing-details-page');
  }

}
